import React, { useState } from 'react'

import { ButtonV2 as Button } from '~/components/button'
import Icon from '~/components/icon'
import { i18n } from '~/i18n'
import { creditName } from '~/utils/credit'
import formatCurrency from '~/utils/format-currency'

import {
  CreditAmount,
  CreditCurrency,
  CreditDetailHeading,
  CreditDetailLine,
  CreditDetailWrapper,
  CreditName,
  CreditPassName,
  CreditValueWrapper,
  Flex,
  HeaderContainer,
  NoWrap,
  ResetButton,
} from './credit-summary.styles'

import type { CreditWithProfileProduct } from '../profiles-credits-summary.types'

type Props = {
  credit: CreditWithProfileProduct
  disableCreditDetailsBackground?: boolean
  onReset?: (arg0: number) => Promise<void>
}

function FullCreditInfo({ credit, disableCreditDetailsBackground }: Props) {
  return (
    <CreditDetailWrapper
      disableCreditDetailsBackground={disableCreditDetailsBackground}
    >
      {credit.manual ? (
        <CreditDetailLine>
          {i18n.t('components.credit_summary.manual_credit')}
        </CreditDetailLine>
      ) : (
        <>
          {credit.details.map((detail, index) => {
            const heading = i18n.t(`credits.details.${detail.key}.heading`)
            const subheading = i18n.t(
              `credits.details.${detail.key}.subheading`,
              {
                defaultValue: '',
              },
            )

            return (
              <CreditDetailLine key={index}>
                <div>
                  <CreditDetailHeading>{heading}</CreditDetailHeading>
                  {subheading && (
                    <>
                      {' '}
                      <CreditDetailHeading>{subheading}</CreditDetailHeading>
                    </>
                  )}
                  {':'}
                </div>
                <div>{detail.value}</div>
              </CreditDetailLine>
            )
          })}
        </>
      )}
    </CreditDetailWrapper>
  )
}

export default function CreditSummary({
  credit,
  disableCreditDetailsBackground,
  onReset,
}: Props) {
  const [expanded, setExpanded] = useState(false)

  const redeemed = credit.status == 'redeemed'
  const expired = credit.status == 'guest_expired'

  return (
    <>
      <HeaderContainer
        $greyOut={redeemed || expired}
        onClick={() => setExpanded(!expanded)}
      >
        <CreditName>{creditName(credit)}</CreditName>
        <NoWrap>
          <CreditValueWrapper>
            <CreditAmount>{formatCurrency(credit.amount)}</CreditAmount>
            <CreditCurrency>{credit.currency}</CreditCurrency>
          </CreditValueWrapper>
          <Button type="unstyled">
            <Icon glyph={expanded ? 'chevron-up' : 'chevron-down'} />
          </Button>
        </NoWrap>
      </HeaderContainer>
      <Flex>
        <CreditPassName onClick={() => setExpanded(!expanded)}>
          {credit.profileProduct.productName}
          {redeemed && credit.creditRedemption && (
            <>
              {' • '}
              {i18n.t(
                'components.profiles_credits_summary.credit_summary.applied',
                {
                  appliedDate: i18n.l(
                    'date.formats.default',
                    Date.parse(credit.creditRedemption.redeemedAt),
                  ),
                  orderNumber: String(credit.creditRedemption?.orderId),
                },
              )}
            </>
          )}
          {expired && (
            <>
              {' • '}
              {i18n.t(
                'components.profiles_credits_summary.credit_summary.expired',
                {
                  dateExpired: i18n.l(
                    'date.formats.default',
                    credit.dateExpired,
                  ),
                },
              )}
            </>
          )}
        </CreditPassName>
        {onReset && redeemed && (
          <ResetButton
            data-testid="reset-credit-button"
            confirmationText="Are you sure you want to reset this credit?"
            onClick={() => onReset(credit.id)}
          >
            RESET CREDIT
          </ResetButton>
        )}
      </Flex>
      {expanded && (
        <FullCreditInfo
          credit={credit}
          disableCreditDetailsBackground={disableCreditDetailsBackground}
        />
      )}
    </>
  )
}
